import React, { useMemo } from "react";
import { useSiteContext } from "../../../state/SiteContext";
import { useLangFilter } from "../../../state/FilterContext";

interface TermProps {
    term: string,
    language?: string,
}

/***
 * A source component that returns the value of localized term.
 * The language is optional and defaults to the site's default language.
 * 
 * Params:
 *      term: The term to return
 *      language: The language to return the term in
 */
export const Term = React.memo(function Term({ term, language }: TermProps): React.ReactElement {
    const { siteState } = useSiteContext();
    const languageFilter = useLangFilter();

    const termValue = useMemo(() => {
        const lang = language || languageFilter || siteState.site?.defaults?.language?.toString() || "en";

        // First look up the term in the theme terms
        const termDef = siteState.theme?.terms?.find((termDef) => termDef.key === term);
        if (termDef) {
            const langTerm = termDef.langs.find((langTerm) => langTerm.lang === lang);
            if (langTerm) {
                return langTerm.value;
            }
        }
        // If not found in the theme terms, look up in the shared terms
        return siteState.terms?.[term]?.[lang] || term;
    }, [term, language, languageFilter, siteState]);

    return <>{termValue}</>;
});